import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Shield, Clock, Award, FileText, Headphones, ExternalLink } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Managed Services', path: '/services#managed-services' },
    { name: 'Cloud Services', path: '/services#cloud-services' },
    { name: 'IT Risk Compliance', path: '/services#compliance' },
    { name: 'Physical Security', path: '/services#video-surveillance' },
    { name: 'VoIP Telecom', path: '/services#voip' },
    { name: 'Fractional CTO/CISO', path: '/services#fractional-cto' },
  ];

  const company = [
    { name: 'About Us', path: '/about' },
    { name: 'Our Process', path: '/process' },
    { name: 'Industries', path: '/industries' },
    { name: 'Contact', path: '/contact' },
  ];

  const legal = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'SMS Terms', path: '/sms-terms' },
  ];

  const supportLinks = [
    { name: 'Windows Agent', url: 'https://mimoIT.servicedesk.atera.com/api/utils/agent-install/windows/?cid=1&aeid=1e0163a1d8e346bbb568e941088c44f9' },
    { name: 'Mac Agent', url: 'https://mimoit.servicedesk.atera.com/api/utils/AgentInstallScript/Mac/001D000002CD8uMIAT?customerId=1' },
    { name: 'Ticket Portal', url: 'https://mimoit.servicedesk.atera.com/' },
  ];

  const licenses = [
    { state: 'NY', type: 'Alarm License', number: '12000319201' },
    { state: 'NJ', type: 'Alarm License', number: '34BA00198300' },
    { state: 'NJ', type: 'Telecom Contractor', number: '34TC00300200' },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-mimo-primary to-mimo-accent py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-4">
            Ready to Transform Your IT Infrastructure?
          </h2>
          <p className="text-white/80 mb-6 max-w-2xl mx-auto">
            Get a free IT assessment and discover how mimoIT can help secure and optimize your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-mimo-primary hover:bg-slate-100 rounded-full px-8 py-3 font-semibold transition-all"
              data-testid="footer-cta-assessment"
            >
              Schedule IT Assessment
            </Link>
            <a 
              href="tel:201-473-4443" 
              className="border-2 border-white text-white hover:bg-white hover:text-mimo-primary rounded-full px-8 py-3 font-semibold transition-all flex items-center justify-center gap-2"
              data-testid="footer-cta-call"
            >
              <Phone className="w-5 h-5" />
              Call 201-473-4443
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/b5x6fyyr_6v2.png" 
                alt="mimoIT Technology Solutions" 
                className="h-14 md:h-16 w-auto object-contain"
              />
            </Link>
            <p className="text-slate-400 text-sm mb-4 leading-relaxed">
              <span className="text-mimo-secondary font-medium">MIMO</span> = Multiple-Input Multiple-Output. Like the technology we're named after, we amplify your business through integrated solutions.
            </p>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Enterprise-grade IT, cybersecurity, and physical security solutions for small and mid-market businesses.
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold font-heading mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link 
                    to={service.path}
                    className="text-slate-400 hover:text-mimo-secondary transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-lg font-bold font-heading mb-6">Company</h4>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.name}>
                  <Link 
                    to={item.path}
                    className="text-slate-400 hover:text-mimo-secondary transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Client Support */}
            <h4 className="text-lg font-bold font-heading mt-8 mb-4 flex items-center gap-2">
              <Headphones className="w-5 h-5 text-mimo-secondary" />
              Client Support
            </h4>
            <ul className="space-y-2">
              {supportLinks.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-mimo-secondary transition-colors flex items-center gap-1 text-sm"
                  >
                    {link.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold font-heading mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:201-473-4443" 
                  className="flex items-start gap-3 text-slate-400 hover:text-mimo-secondary transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>201-473-4443</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@mimoit.com" 
                  className="flex items-start gap-3 text-slate-400 hover:text-mimo-secondary transition-colors"
                  data-testid="footer-info-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="block">info@mimoit.com</span>
                    <span className="text-xs text-slate-500">General inquiries</span>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:service@mimoit.com" 
                  className="flex items-start gap-3 text-slate-400 hover:text-mimo-secondary transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="block">service@mimoit.com</span>
                    <span className="text-xs text-slate-500">Service requests</span>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p>31 Vose Ave, Unit 6</p>
                  <p>South Orange, NJ 07079</p>
                </div>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p>450 Lexington Ave #3101</p>
                  <p>New York, NY 10163</p>
                </div>
              </li>
            </ul>
          </div>

          {/* State and Federal Certifications, Licenses, and Clearances */}
          <div>
            <h4 className="text-lg font-bold font-heading mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-mimo-secondary" />
              Licenses
            </h4>
            <ul className="space-y-3">
              {licenses.map((license, index) => (
                <li key={index} className="text-slate-400 text-sm">
                  <span className="font-medium text-white">{license.state}</span> {license.type}
                  <br />
                  <span className="text-slate-500">Lic# {license.number}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-wrap justify-center gap-8 mb-8">
            <div className="flex items-center gap-2 text-slate-400">
              <Shield className="w-5 h-5 text-mimo-secondary" />
              <span className="text-sm">NIST Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Award className="w-5 h-5 text-mimo-secondary" />
              <span className="text-sm">Certified MBE</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-5 h-5 text-mimo-secondary" />
              <span className="text-sm">24/7 Support</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Shield className="w-5 h-5 text-mimo-secondary" />
              <span className="text-sm">Tri-State Licensed</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} mimoIT. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-mimo-secondary transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-mimo-secondary transition-colors">Terms of Service</Link>
            <Link to="/sms-terms" className="hover:text-mimo-secondary transition-colors">SMS Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;